import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faKey,
  faEye,
  faEyeSlash,
  faWandMagicSparkles,
  faCopy,
  faCheck,
  faCircleCheck,
  faCircle,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import './ResetPassword.css';

const API = process.env.REACT_APP_API_BASE_URL;
const MIN_LENGTH = 8; // same as before

// easy to read out loud / type: no 0/O, 1/l/I
const CHARS = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const makePassword = (len = 10) => {
  const buf = new Uint32Array(len);
  window.crypto.getRandomValues(buf);
  return Array.from(buf, (n) => CHARS[n % CHARS.length]).join('');
};

const ResetPassword = ({ teacherId }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [lastSaved, setLastSaved] = useState(''); // so the admin can copy it after saving
  const [copied, setCopied] = useState(false);

  // success message hides after 3s (same as before)
  useEffect(() => {
    if (!success) return undefined;
    const t = setTimeout(() => setSuccess(false), 3000);
    return () => clearTimeout(t);
  }, [success]);

  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  const longEnough = password.length >= MIN_LENGTH;
  const matches = password.length > 0 && password === confirmPassword;

  const generate = () => {
    const p = makePassword();
    setPassword(p);
    setConfirmPassword(p);
    setShow(true);
    setError('');
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  // ---------- Save (same endpoint + same body) ----------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('Passwords don’t match');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await axios.put(
        `${API}/api/admin/studentprofile/password/${teacherId}`,
        { password },
        { headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } }
      );
      setLastSaved(password);
      setSuccess(true);
      setPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Update failed.');
    } finally {
      setLoading(false);
    }
  };

  const onType = (setter) => (e) => {
    setter(e.target.value);
    setError('');
    setLastSaved('');
  };

  return (
    <section className="rmarp">
      <header className="rmarp-head">
        <span className="rmarp-icon">
          <FontAwesomeIcon icon={faKey} />
        </span>
        <div>
          <h2>Reset password</h2>
          <p>Set a new password for this account. After saving you can copy it and send it to them.</p>
        </div>
      </header>

      <form onSubmit={handleSubmit} className="rmarp-form">
        <label className="rmarp-field">
          <span>New password</span>
          <div className="rmarp-input">
            <input
              type={show ? 'text' : 'password'}
              value={password}
              onChange={onType(setPassword)}
              required
              minLength={MIN_LENGTH}
              autoComplete="new-password"
              disabled={loading}
            />
            <button type="button" onClick={() => setShow((s) => !s)} aria-label={show ? 'Hide password' : 'Show password'}>
              <FontAwesomeIcon icon={show ? faEyeSlash : faEye} />
            </button>
          </div>
        </label>

        <label className="rmarp-field">
          <span>Confirm password</span>
          <div className="rmarp-input">
            <input
              type={show ? 'text' : 'password'}
              value={confirmPassword}
              onChange={onType(setConfirmPassword)}
              required
              autoComplete="new-password"
              disabled={loading}
            />
          </div>
        </label>

        <ul className="rmarp-checks">
          <li className={longEnough ? 'is-ok' : ''}>
            <FontAwesomeIcon icon={longEnough ? faCircleCheck : faCircle} /> At least {MIN_LENGTH} characters
          </li>
          <li className={matches ? 'is-ok' : ''}>
            <FontAwesomeIcon icon={matches ? faCircleCheck : faCircle} /> Both passwords match
          </li>
        </ul>

        {error && (
          <p className="rmarp-msg is-error" role="alert">
            <FontAwesomeIcon icon={faTriangleExclamation} /> {error}
          </p>
        )}

        {lastSaved && (
          <div className="rmarp-msg is-ok" role="status">
            <FontAwesomeIcon icon={faCircleCheck} />
            <span>
              {success ? 'Password updated. ' : ''}New password: <code>{lastSaved}</code>
            </span>
            <button type="button" className="rmarp-copy" onClick={() => copy(lastSaved)}>
              <FontAwesomeIcon icon={copied ? faCheck : faCopy} /> {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        )}

        <div className="rmarp-actions">
          <button type="button" className="rmarp-btn rmarp-btn--ghost" onClick={generate} disabled={loading}>
            <FontAwesomeIcon icon={faWandMagicSparkles} /> Generate
          </button>
          <button type="submit" className="rmarp-btn" disabled={loading || !longEnough || !matches}>
            <FontAwesomeIcon icon={faKey} /> {loading ? 'Updating…' : 'Reset password'}
          </button>
        </div>
      </form>
    </section>
  );
};

export default ResetPassword;